import {
  Button,
  ErrorBoundary,
  FullScreenLoading,
  Icon,
  ServerTable,
  TableErrorDisplay,
  Text,
} from '@civilgrid/ui/components';
import { isEmptyFilterGroup, type TableStateControl, useTableSettings, useTableStoreState } from '@civilgrid/ui/hooks';
import { Link } from '@tanstack/react-router';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { debounce } from 'lodash';
import { useCallback, useEffect, useId, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { OrgFeatureFlag } from '~/authorizations/FeatureFlags';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { useConflictsSummaryPath } from '~/hooks/useConflictsSummaryPath';
import { useOrgFeatureFlag } from '~/hooks/useOrgFeatureFlag';
import ProtectedRouteLayout from '~/pages/common/ProtectedRouteLayout';
import rootStore from '~/store';
import { columns } from './columns';
import { useGetDataRefreshStatus } from './queries';

const TABLE_ID = 'data-refresh-status-table';

/**
 * Data Refresh Status page (/conflictsSummary/dataRefreshStatus): per-jurisdiction
 * refresh/research status for the org's Paving & Moratoria data. Server-side
 * pagination/sort/filter via ServerTable + useTableStoreState (component-local state, homeViewV2 pattern).
 * Gated by the org conflictsSummary flag.
 */
export default function DataRefreshStatus() {
  const headingId = useId();
  useOrgFeatureFlag({ flag: OrgFeatureFlag.ConflictsSummary, redirectTo: '/home' });
  const { currentOrganizationId } = useSnapshot(rootStore) as typeof rootStore;
  // Parent page target: the user's most recent Conflicts Summary view.
  const conflictsSummaryPath = useConflictsSummaryPath();

  useEffect(() => {
    if (currentOrganizationId) {
      capturePosthogEvent(POSTHOG_EVENTS.dataRefreshStatusViewed, { organization_id: currentOrganizationId });
    }
  }, [currentOrganizationId]);

  const { initialPageSize } = useTableSettings({ id: TABLE_ID });
  // Component-local state (homeViewV2 pattern) — independent state cells, so composing setters
  // (e.g. clear filters + clear search in one handler) is safe, unlike the URL-backed variant.
  // Persisted page size seeds the initial state; the reset default stays DEFAULT_PAGE_SIZE.
  const baseStateControl = useTableStoreState({
    initialPagination: initialPageSize != null ? { pageIndex: 0, pageSize: initialPageSize } : undefined,
  });

  const trackSearch = useMemo(
    () =>
      debounce((value: string) => {
        capturePosthogEvent(POSTHOG_EVENTS.dataRefreshStatusSearchChanged, {
          has_query: value.length > 0,
          query_length: value.length,
          organization_id: currentOrganizationId,
        });
      }, 500),
    [currentOrganizationId],
  );

  // Flush-proof unmount: a pending trailing invocation must not fire after navigation away.
  useEffect(() => () => trackSearch.cancel(), [trackSearch]);

  // Wrap the state-control setters so every sort/filter/search/pagination change is captured,
  // no matter which ServerTable control triggered it.
  const stateControl = useMemo<TableStateControl>(
    () => ({
      ...baseStateControl,
      setSorting: (updaterOrValue) => {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(baseStateControl.sorting) : updaterOrValue;
        if (next[0]) {
          capturePosthogEvent(POSTHOG_EVENTS.dataRefreshStatusSortChanged, {
            column_id: next[0].id,
            direction: next[0].desc ? 'desc' : 'asc',
            organization_id: currentOrganizationId,
          });
        }
        baseStateControl.setSorting(next as SortingState);
      },
      setFilters: (newFilters) => {
        capturePosthogEvent(POSTHOG_EVENTS.dataRefreshStatusFiltersChanged, {
          filter_count: newFilters.items.length,
          organization_id: currentOrganizationId,
        });
        baseStateControl.setFilters(newFilters);
      },
      resetFilters: () => {
        capturePosthogEvent(POSTHOG_EVENTS.dataRefreshStatusFiltersChanged, {
          filter_count: 0,
          organization_id: currentOrganizationId,
        });
        baseStateControl.resetFilters();
      },
      setGlobalFilter: (value) => {
        trackSearch(value);
        baseStateControl.setGlobalFilter(value);
      },
      setPagination: (updaterOrValue) => {
        const next =
          typeof updaterOrValue === 'function' ? updaterOrValue(baseStateControl.pagination) : updaterOrValue;
        capturePosthogEvent(POSTHOG_EVENTS.dataRefreshStatusPageChanged, {
          page_index: next.pageIndex,
          page_size: next.pageSize,
          organization_id: currentOrganizationId,
        });
        baseStateControl.setPagination(next as PaginationState);
      },
      setCurrentPage: (page) => {
        capturePosthogEvent(POSTHOG_EVENTS.dataRefreshStatusPageChanged, {
          page_index: page - 1,
          page_size: baseStateControl.pagination.pageSize,
          organization_id: currentOrganizationId,
        });
        baseStateControl.setCurrentPage(page);
      },
      setPageSize: (size) => {
        capturePosthogEvent(POSTHOG_EVENTS.dataRefreshStatusPageChanged, {
          page_index: 0,
          page_size: size,
          organization_id: currentOrganizationId,
        });
        baseStateControl.setPageSize(size);
      },
      resetToDefaults: () => {
        capturePosthogEvent(POSTHOG_EVENTS.dataRefreshStatusFiltersChanged, {
          filter_count: 0,
          organization_id: currentOrganizationId,
        });
        capturePosthogEvent(POSTHOG_EVENTS.dataRefreshStatusPageChanged, {
          page_index: 0,
          page_size: baseStateControl.defaultPageSize,
          organization_id: currentOrganizationId,
        });
        baseStateControl.resetToDefaults?.();
      },
    }),
    [baseStateControl, trackSearch, currentOrganizationId],
  );

  const { pagination, sorting, filters, globalFilter, setGlobalFilter, resetFilters } = stateControl;

  const { data, isLoading, isFetching, error } = useGetDataRefreshStatus({
    organizationId: currentOrganizationId,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    sorting,
    filters,
    globalFilter,
    enabled: !!currentOrganizationId,
  });

  const tableData = useMemo(
    () => ({
      data: data?.items ?? [],
      rowCount: data?.count ?? 0,
      isLoading,
      isFetching,
      error: error ?? null,
    }),
    [data, isLoading, isFetching, error],
  );

  const hasActiveFilters = useMemo(
    () => !isEmptyFilterGroup(filters) || globalFilter.trim().length > 0,
    [filters, globalFilter],
  );

  const handleClearAllFilters = useCallback(() => {
    resetFilters();
    setGlobalFilter('');
  }, [resetFilters, setGlobalFilter]);

  const emptyState = useMemo(() => {
    if (tableData.rowCount > 0 || isLoading || isFetching) return null;
    if (hasActiveFilters) {
      return (
        <div className="flex flex-col items-center gap-4 text-center">
          <Text className="text-grey-80">No records to show based on current filters.</Text>
          <Button
            variant="secondary"
            size="small"
            onClick={handleClearAllFilters}
            data-testid="data-refresh-status-clear-all-filters"
          >
            <Icon icon="crossCircled" size={14} /> Clear all filters
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <Text className="text-grey-80">No refresh status records for this organization yet.</Text>
      </div>
    );
  }, [tableData.rowCount, isLoading, isFetching, hasActiveFilters, handleClearAllFilters]);

  return (
    <ProtectedRouteLayout includeOrgProjectSwitcher includeSearch={false}>
      <div className="flex h-full min-h-0 flex-col gap-4 p-4" data-testid="data-refresh-status-page">
        <div className="flex flex-col gap-1">
          <Link
            to={conflictsSummaryPath}
            className="flex items-center gap-1.5 self-start border-b border-transparent text-sm text-blue-50 no-underline hover:border-blue-50"
            data-testid="data-refresh-status-back-link"
          >
            <Icon icon="arrowLeft" size={16} />
            <span>Back</span>
          </Link>
          <h1 id={headingId} className="text-xl font-semibold text-blue-80">
            Paving &amp; Moratoria Data - Refresh Status
          </h1>
        </div>

        <Text className="text-xs text-grey-90" data-testid="data-refresh-status-note">
          <span className="font-semibold">Note:</span> Last-refreshed dates come from loaded data automatically;
          research status and notes are maintained by your CivilGrid team.
        </Text>

        <ErrorBoundary
          fallback={(err) => <TableErrorDisplay error={err} />}
          onError={(e, info) => console.error('DataRefreshStatus table error:', e, info)}
        >
          <div className="flex min-h-0 flex-1 flex-col" data-testid="data-refresh-status-table-container">
            <ServerTable
              id={TABLE_ID}
              aria-labelledby={headingId}
              columns={columns}
              data={tableData}
              stateControl={stateControl}
              enableRowSelection={false}
              enableColumnGrouping={false}
              className="h-full"
              isLoading={tableData.isLoading}
              isFetching={tableData.isFetching}
              emptyState={emptyState}
            />
          </div>
        </ErrorBoundary>
      </div>
    </ProtectedRouteLayout>
  );
}
